import React from "react";
import { useLocation } from "react-router-dom";
import { Box, IconButton, ToggleButton } from "@mui/material";

import { DocumentActionsStatusState, useDocumentActionsStatus } from "PdfDocumentGrid/hooks/DocumentActionsStatus.context";
import { APPLICABLE_STATE_NAME } from "utils/DexieDBManager/dexieDB.models";
import { DOCUMENT_VERSION_TYPE } from "utils/i18n/resources";
import {
  StyledBadge,
  styledCategoriesList,
  styledCategorySelector,
  styledSynchronizationItem,
} from "./stylesPdfDocumentsList";

import {
  IDocumentVersion,
  ISynchronizationItem,
  ISynchronizationItemMeta,
  getSynchronizationItemById,
} from "utils/DexieDBManager";

import FontAwesomeSvgIcon from "Layouts/Common/FontAwesomeSvgIcon";
import svgFronts from "Layouts/Common/fontAwesome";
import { filterDocumentByTitle, sortDocuments } from "./helpers";
import { ORDER_VALUES } from "./FilterDocumentCards";
import { isPwa } from "utils/pwa";

type ChoosenDocumentVersionType = {
  starredDocumentsCount: number;
  documentVersions: IDocumentVersion[];
  documentVersionType: DOCUMENT_VERSION_TYPE;
};

interface ICategorySelectorProps {
  page: number;
  globalSearchQuery: string;
  choosenSynchronizationItem?: ISynchronizationItem;
  synchronizationItemsFromDb: ISynchronizationItemMeta[];
  synchronizationItemsFromServer: ISynchronizationItem[];
  setDocumentVersionsToDisplay: (synchronizationItem: IDocumentVersion[]) => void;
  setChoosenDocumentVersion: (documentVersion: ChoosenDocumentVersionType) => void;
  setChoosenSynchronizationItem: (synchronizationItem: ISynchronizationItem) => void;
}

const CategorySelector: React.FunctionComponent<ICategorySelectorProps> = ({
  page,
  globalSearchQuery,
  choosenSynchronizationItem,
  synchronizationItemsFromDb,
  synchronizationItemsFromServer,
  setChoosenSynchronizationItem,
  setDocumentVersionsToDisplay,
  setChoosenDocumentVersion,
}) => {
  const { state, dispatch } = useDocumentActionsStatus();
  const { pathname } = useLocation();

  const boxOfCategoriesRef = React.useRef<HTMLDivElement>(null);

  const isOnLine = navigator.onLine;
  const isPwaAndOffline = isPwa && !isOnLine;

  let computedSynchronizationItemsData = isPwaAndOffline 
                                          ? synchronizationItemsFromDb
                                          : synchronizationItemsFromServer;

  const handleOnSynchronizationItemChange = async (synchronizationItemId: string) => {
    let synchronizationItem: ISynchronizationItem;

    let applicablesDocumentVersionsToDisplay: IDocumentVersion[] = [];
    let nextApplicablesDocumentVersionsToDisplay: IDocumentVersion[] = [];
    
    if (isPwaAndOffline) {
      synchronizationItem = (await getSynchronizationItemById(synchronizationItemId)) as ISynchronizationItem;
    } else {
      synchronizationItem = synchronizationItemsFromServer.find((item) => item.id === synchronizationItemId) as ISynchronizationItem;
    }

    const applicables = synchronizationItem?.applicables ?? [];
    const nextApplicables = synchronizationItem?.nextApplicables ?? [];

    if(isPwaAndOffline && pathname === "/acknowledgement") { 
      applicablesDocumentVersionsToDisplay = getAcknowledgementDocuments(applicables);
      nextApplicablesDocumentVersionsToDisplay = getAcknowledgementDocuments(nextApplicables)
    } else if(isPwaAndOffline && pathname === "/starred") {
      applicablesDocumentVersionsToDisplay = getStarredDocuments(applicables);
      nextApplicablesDocumentVersionsToDisplay = getStarredDocuments(nextApplicables)
    } else {
      applicablesDocumentVersionsToDisplay = applicables;
      nextApplicablesDocumentVersionsToDisplay = nextApplicables;
    }
    
    const filteredApplicableDocuments = applicablesDocumentVersionsToDisplay
                                        .filter(filterDocumentByTitle(globalSearchQuery));

    const filteredNextApplicableDocuments = nextApplicablesDocumentVersionsToDisplay
                                        .filter(filterDocumentByTitle(globalSearchQuery));
    
    const sortedApplicableDocuments = filteredApplicableDocuments
                                        .sort(sortDocuments(ORDER_VALUES.TITLE))
                                        .slice(page * 0, page * 30);

  const initDocumentVersionState = (type: APPLICABLE_STATE_NAME) => (documentVersion: IDocumentVersion) => {
    const parentId = documentVersion.parentDocument.id;
    const status = state.documentActionsStatus.get(parentId);
    
    if (!status?.isInitialized) {
      dispatch({
        type: "init",
        initialDocumentStatus: {
          documentVersionType: type,
          parentDocumentId: parentId,
          isAckRequired: documentVersion.uds.isAck,
          isStarredDocument: documentVersion.uds.isFav,
          isUnread: !Boolean(documentVersion.uds.lastRead),
        },
      });
    }
  };

  filteredApplicableDocuments.forEach(initDocumentVersionState(APPLICABLE_STATE_NAME.APPLICABLE));

  filteredNextApplicableDocuments.forEach(initDocumentVersionState(APPLICABLE_STATE_NAME.NEXT_APPLICABLE));

    setChoosenSynchronizationItem(synchronizationItem);
    setChoosenDocumentVersion({
      documentVersions: sortedApplicableDocuments,
      documentVersionType: DOCUMENT_VERSION_TYPE.APPLICABLES,
      starredDocumentsCount: synchronizationItem?.starredDocumentsCount?.applicables || 0,
    });
                                        
    setDocumentVersionsToDisplay(sortedApplicableDocuments);
  };

  const handleScroll = (scrollOffset: number) => {
    if (boxOfCategoriesRef.current) {
      boxOfCategoriesRef.current.scrollLeft += scrollOffset;
    }
  };

  return (
    <Box
      id="categorySelector"
      sx={{
        ...styledCategorySelector,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
        <IconButton
          onClick={() => handleScroll(-200)}
          aria-label="Previous categories"
          size="small"
        >
          <FontAwesomeSvgIcon icon={svgFronts.faChevronLeft} />
        </IconButton>

        <Box
          sx={styledCategoriesList}
          ref={boxOfCategoriesRef}
          className="scroll-categories"
        >
          {computedSynchronizationItemsData
            .map((synchronizationItem) => (
              <ToggleButton
                size="small"
                value="check"
                disableRipple={true}
                key={synchronizationItem.id}
                sx={styledSynchronizationItem}
                selected={synchronizationItem.id === choosenSynchronizationItem?.id}
                onChange={() => handleOnSynchronizationItemChange(synchronizationItem.id)}
              >
                <StyledBadge
                  badgeContent={computeBadgeContent(state, synchronizationItem, pathname)}
                  selected={synchronizationItem.id === choosenSynchronizationItem?.id}
                  showZero={true}
                  color="primary"
                >
                  <span>{synchronizationItem.name}</span>
                </StyledBadge>
              </ToggleButton>
            ))}
        </Box>

        <IconButton
          onClick={() => handleScroll(200)}
          aria-label="Next categories"
          size="small"
        >
          <FontAwesomeSvgIcon icon={svgFronts.faChevronRight} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CategorySelector;

function computeBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem,
  pathname: string
): React.ReactNode {
  const isStarredPage = pathname === "/starred";
  const isAcknowledgementPage = pathname === "/acknowledgement";

  if (isStarredPage) {
    return computeStarredBadgeContent(state, synchronizationItem);
  }

  if (isAcknowledgementPage) {
    return computeAcknowledgementBadgeContent(state, synchronizationItem);
  }

  return computeCategoriesBadgeContent(state, synchronizationItem);
}

function computeCategoriesBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem
): React.ReactNode {
  const categoriesIsAckRequiredDocumentsState = state.categoriesIsAckRequiredDocumentsState.get(synchronizationItem.id);

  const isAckRequiredApplicableDocumentsCount = categoriesIsAckRequiredDocumentsState?.isAckRequiredApplicableDocumentsCount ?? 0;
  const isAckRequiredNextApplicableDocumentsCount = categoriesIsAckRequiredDocumentsState?.isAckRequiredNextApplicableDocumentsCount ?? 0;
  const isAckRequiredDocumentsVersionCount = isAckRequiredApplicableDocumentsCount + isAckRequiredNextApplicableDocumentsCount;

  const applicablesDocumentsCount = categoriesIsAckRequiredDocumentsState?.applicablesDocumentsCount ?? 0;
  const nextApplicablesDocumentsCount = categoriesIsAckRequiredDocumentsState?.nextApplicablesDocumentsCount ?? 0;

  const documentsVersionCount = applicablesDocumentsCount + nextApplicablesDocumentsCount;

  return `${isAckRequiredDocumentsVersionCount}/${documentsVersionCount}`;
}

function computeStarredBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem
): React.ReactNode {
  const starredIsAckRequiredDocumentsState = state.starredIsAckRequiredDocumentsState.get(synchronizationItem.id);

  const isAckRequiredApplicableDocumentsCount = starredIsAckRequiredDocumentsState?.isAckRequiredApplicableDocumentsCount ?? 0;
  const isAckRequiredNextApplicableDocumentsCount = starredIsAckRequiredDocumentsState?.isAckRequiredNextApplicableDocumentsCount ?? 0;
  const isAckRequiredDocumentsVersionCount = isAckRequiredApplicableDocumentsCount + isAckRequiredNextApplicableDocumentsCount;

  const applicablesDocumentsCount = starredIsAckRequiredDocumentsState?.applicablesDocumentsCount ?? 0;
  const nextApplicablesDocumentsCount = starredIsAckRequiredDocumentsState?.nextApplicablesDocumentsCount ?? 0;

  const documentsVersionCount = applicablesDocumentsCount + nextApplicablesDocumentsCount;

  return `${isAckRequiredDocumentsVersionCount}/${documentsVersionCount}`;
}

function computeAcknowledgementBadgeContent(
  state: DocumentActionsStatusState,
  synchronizationItem: ISynchronizationItemMeta | ISynchronizationItem
): React.ReactNode {
  const acknowledgementIsAckRequiredDocumentsState = state.acknowledgementIsAckRequiredDocumentsState.get(synchronizationItem.id);

  const isAckRequiredApplicableDocumentsCount = acknowledgementIsAckRequiredDocumentsState?.isAckRequiredApplicableDocumentsCount ?? 0;
  const isAckRequiredNextApplicableDocumentsCount = acknowledgementIsAckRequiredDocumentsState?.isAckRequiredNextApplicableDocumentsCount ?? 0;

  return `${isAckRequiredApplicableDocumentsCount + isAckRequiredNextApplicableDocumentsCount}`;
}


const getAcknowledgementDocuments = (documentVersions: IDocumentVersion[]) => {
  return documentVersions.filter((app) => app.uds.isAck);
};

export const getStarredDocuments = (documentVersions: IDocumentVersion[]) => {
  return documentVersions.filter((app) => app.uds.isFav);
};
